/**
 * API client functions for the Annotation/Issues system.
 *
 * All functions use the api client with automatic snake_case/camelCase conversion.
 */

import { api } from '@/lib/api'
import type {
  Annotation,
  AnnotationListResponse,
  AnnotationFilters,
  IssueCreate,
  SelfNoteCreate,
  RoleCommentCreate,
  AnnotationUpdate,
  StatusChangeRequest,
  Comment,
  CommentCreate,
  Attachment,
  AttachmentListResponse,
  AnnotationLink,
  LinkCreate,
  LinkListResponse,
  HistoryListResponse,
  ValueComparison,
  SnapshotComparison,
  BulkStatusChangeRequest,
  BulkAssignRequest,
  BulkMarkReadRequest,
  BulkOperationResponse,
  RecordCountRequest,
  RecordCountResponse,
  IssueMetricsSummary,
  AgingReport,
  IssuesByGroupResponse,
  TurnaroundReport,
  SingleIssueExportData,
  AnnotationType,
  UserIssueMetrics,
} from '@/types/annotation'

const BASE_PATH = (studyId: string) => `/studies/${studyId}/annotations`

// =============================================================================
// Issues CRUD
// =============================================================================

/**
 * Create a new issue.
 */
export async function createIssue(studyId: string, data: IssueCreate): Promise<Annotation> {
  // Convert to snake_case for API
  const payload = {
    dataset_name: data.datasetName,
    record_key: data.recordKey,
    field_name: data.fieldName,
    content: data.content,
    source_module_key: data.sourceModuleKey,
    source_page_key: data.sourcePageKey,
    category: data.category,
    priority: data.priority,
    assigned_to_user_id: data.assignedToUserId,
    assigned_to_role: data.assignedToRole,
    value_at_creation: data.valueAtCreation,
    send_notification: data.sendNotification ?? true,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/issues`, payload)
  return response.data
}

/**
 * List issues with optional filters.
 */
export async function listIssues(
  studyId: string,
  options?: {
    page?: number
    pageSize?: number
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    filters?: AnnotationFilters
  }
): Promise<AnnotationListResponse> {
  const params: Record<string, unknown> = {
    page: options?.page ?? 1,
    page_size: options?.pageSize ?? 50,
    sort_by: options?.sortBy,
    sort_order: options?.sortOrder,
  }

  // Add filter params
  if (options?.filters) {
    const f = options.filters
    if (f.annotationTypes?.length) params.annotation_types = f.annotationTypes.join(',')
    if (f.statuses?.length) params.statuses = f.statuses.join(',')
    if (f.categories?.length) params.categories = f.categories.join(',')
    if (f.priorities?.length) params.priorities = f.priorities.join(',')
    if (f.datasetName) params.dataset_name = f.datasetName
    if (f.assignedToUserId) params.assigned_to_user_id = f.assignedToUserId
    if (f.assignedToRole) params.assigned_to_role = f.assignedToRole
    if (f.createdBy) params.created_by = f.createdBy
    if (f.isRead !== undefined) params.is_read = f.isRead
    if (f.sourceModuleKey) params.source_module_key = f.sourceModuleKey
    if (f.sourcePageKey) params.source_page_key = f.sourcePageKey
    if (f.searchText) params.search_text = f.searchText
    if (f.createdAfter) params.created_after = f.createdAfter
    if (f.createdBefore) params.created_before = f.createdBefore
  }

  const response = await api.get(`${BASE_PATH(studyId)}/issues`, { params })
  return response.data
}

/**
 * Get a single issue by ID.
 */
export async function getIssue(studyId: string, annotationId: number): Promise<Annotation> {
  const response = await api.get(`${BASE_PATH(studyId)}/issues/${annotationId}`)
  return response.data
}

/**
 * Get any annotation by ID (issue, self-note, or role-comment).
 */
export async function getAnnotation(studyId: string, annotationId: number): Promise<Annotation> {
  const response = await api.get(`${BASE_PATH(studyId)}/detail/${annotationId}`)
  return response.data
}

/**
 * Update an issue.
 */
export async function updateIssue(
  studyId: string,
  annotationId: number,
  data: AnnotationUpdate
): Promise<Annotation> {
  const payload = {
    content: data.content,
    field_name: data.fieldName,
    category: data.category,
    priority: data.priority,
    assigned_to_user_id: data.assignedToUserId,
    assigned_to_role: data.assignedToRole,
  }
  const response = await api.put(`${BASE_PATH(studyId)}/issues/${annotationId}`, payload)
  return response.data
}

/**
 * Change issue status.
 */
export async function changeIssueStatus(
  studyId: string,
  annotationId: number,
  data: StatusChangeRequest
): Promise<Annotation> {
  const payload = {
    new_status: data.newStatus,
    change_reason: data.changeReason,
  }
  const response = await api.patch(`${BASE_PATH(studyId)}/issues/${annotationId}/status`, payload)
  return response.data
}

/**
 * Delete an issue.
 */
export async function deleteIssue(studyId: string, annotationId: number): Promise<void> {
  await api.delete(`${BASE_PATH(studyId)}/issues/${annotationId}`)
}

// =============================================================================
// Self-Notes
// =============================================================================

/**
 * Create a self-note.
 */
export async function createSelfNote(studyId: string, data: SelfNoteCreate): Promise<Annotation> {
  const payload = {
    dataset_name: data.datasetName,
    record_key: data.recordKey,
    field_name: data.fieldName,
    content: data.content,
    source_module_key: data.sourceModuleKey,
    source_page_key: data.sourcePageKey,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/notes`, payload)
  return response.data
}

/**
 * List self-notes for the current user.
 */
export async function listSelfNotes(
  studyId: string,
  options?: {
    page?: number
    pageSize?: number
    datasetName?: string
    searchText?: string
  }
): Promise<AnnotationListResponse> {
  const params: Record<string, unknown> = {
    page: options?.page ?? 1,
    page_size: options?.pageSize ?? 50,
    dataset_name: options?.datasetName,
    search_text: options?.searchText,
  }
  const response = await api.get(`${BASE_PATH(studyId)}/notes`, { params })
  return response.data
}

// =============================================================================
// Role Comments
// =============================================================================

/**
 * Create a role comment.
 */
export async function createRoleComment(
  studyId: string,
  data: RoleCommentCreate
): Promise<Annotation> {
  const payload = {
    dataset_name: data.datasetName,
    record_key: data.recordKey,
    field_name: data.fieldName,
    content: data.content,
    source_module_key: data.sourceModuleKey,
    source_page_key: data.sourcePageKey,
    target_role: data.targetRole,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/role-comments`, payload)
  return response.data
}

/**
 * List role comments visible to the current user.
 */
export async function listRoleComments(
  studyId: string,
  options?: {
    page?: number
    pageSize?: number
    datasetName?: string
    targetRole?: string
    searchText?: string
  }
): Promise<AnnotationListResponse> {
  const params: Record<string, unknown> = {
    page: options?.page ?? 1,
    page_size: options?.pageSize ?? 50,
    dataset_name: options?.datasetName,
    target_role: options?.targetRole,
    search_text: options?.searchText,
  }
  const response = await api.get(`${BASE_PATH(studyId)}/role-comments`, { params })
  return response.data
}

// =============================================================================
// Comments (Replies)
// =============================================================================

/**
 * Add a comment to an annotation.
 */
export async function addComment(
  studyId: string,
  annotationId: number,
  data: CommentCreate
): Promise<Comment> {
  const payload = {
    content: data.content,
    parent_comment_id: data.parentCommentId,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/${annotationId}/comments`, payload)
  return response.data
}

/**
 * List comments for an annotation.
 */
export async function listComments(
  studyId: string,
  annotationId: number
): Promise<{ comments: Comment[]; total: number }> {
  const response = await api.get(`${BASE_PATH(studyId)}/${annotationId}/comments`)
  return response.data
}

/**
 * Delete a comment.
 */
export async function deleteComment(
  studyId: string,
  annotationId: number,
  commentId: number
): Promise<void> {
  await api.delete(`${BASE_PATH(studyId)}/${annotationId}/comments/${commentId}`)
}

// =============================================================================
// Attachments
// =============================================================================

/**
 * Upload an attachment to an annotation.
 */
export async function uploadAttachment(
  studyId: string,
  annotationId: number,
  file: File
): Promise<Attachment> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await api.post(`${BASE_PATH(studyId)}/${annotationId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

/**
 * List attachments for an annotation.
 */
export async function listAttachments(
  studyId: string,
  annotationId: number
): Promise<AttachmentListResponse> {
  const response = await api.get(`${BASE_PATH(studyId)}/${annotationId}/attachments`)
  return response.data
}

/**
 * Download an attachment.
 */
export async function downloadAttachment(
  studyId: string,
  annotationId: number,
  attachmentId: number
): Promise<Blob> {
  const response = await api.get(
    `${BASE_PATH(studyId)}/${annotationId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  )
  return response.data
}

/**
 * Delete an attachment.
 */
export async function deleteAttachment(
  studyId: string,
  annotationId: number,
  attachmentId: number
): Promise<void> {
  await api.delete(`${BASE_PATH(studyId)}/${annotationId}/attachments/${attachmentId}`)
}

// =============================================================================
// Links
// =============================================================================

/**
 * Create a link between annotations.
 */
export async function createLink(
  studyId: string,
  annotationId: number,
  data: LinkCreate
): Promise<AnnotationLink> {
  const payload = {
    target_annotation_id: data.targetAnnotationId,
    link_type: data.linkType,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/${annotationId}/links`, payload)
  return response.data
}

/**
 * List links for an annotation.
 */
export async function listLinks(
  studyId: string,
  annotationId: number
): Promise<LinkListResponse> {
  const response = await api.get(`${BASE_PATH(studyId)}/${annotationId}/links`)
  return response.data
}

/**
 * Delete a link.
 */
export async function deleteLink(
  studyId: string,
  annotationId: number,
  linkId: number
): Promise<void> {
  await api.delete(`${BASE_PATH(studyId)}/${annotationId}/links/${linkId}`)
}

// =============================================================================
// History
// =============================================================================

/**
 * Get history for an annotation.
 */
export async function getHistory(
  studyId: string,
  annotationId: number
): Promise<HistoryListResponse> {
  const response = await api.get(`${BASE_PATH(studyId)}/${annotationId}/history`)
  return response.data
}

// =============================================================================
// Bulk Operations
// =============================================================================

/**
 * Bulk change status of multiple annotations.
 */
export async function bulkChangeStatus(
  studyId: string,
  data: BulkStatusChangeRequest
): Promise<BulkOperationResponse> {
  const payload = {
    annotation_ids: data.annotationIds,
    new_status: data.newStatus,
    change_reason: data.changeReason,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/bulk/status`, payload)
  return response.data
}

/**
 * Bulk assign annotations.
 */
export async function bulkAssign(
  studyId: string,
  data: BulkAssignRequest
): Promise<BulkOperationResponse> {
  const payload = {
    annotation_ids: data.annotationIds,
    assigned_to_user_id: data.assignedToUserId,
    assigned_to_role: data.assignedToRole,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/bulk/assign`, payload)
  return response.data
}

/**
 * Bulk mark annotations as read.
 */
export async function bulkMarkRead(
  studyId: string,
  data: BulkMarkReadRequest
): Promise<BulkOperationResponse> {
  const payload = {
    annotation_ids: data.annotationIds,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/bulk/read`, payload)
  return response.data
}

// =============================================================================
// Record Counts
// =============================================================================

/**
 * Get annotation counts for multiple records.
 */
export async function getRecordCounts(
  studyId: string,
  data: RecordCountRequest
): Promise<RecordCountResponse> {
  const payload = {
    dataset_name: data.datasetName,
    record_keys: data.recordKeys,
  }
  const response = await api.post(`${BASE_PATH(studyId)}/counts`, payload)
  return response.data
}

// =============================================================================
// Value Comparison
// =============================================================================

/**
 * Get value comparison (creation vs current) for an annotation.
 */
export async function getValueComparison(
  studyId: string,
  annotationId: number
): Promise<ValueComparison> {
  const response = await api.get(`${BASE_PATH(studyId)}/${annotationId}/value-comparison`)
  return response.data
}

// =============================================================================
// Snapshot Comparison
// =============================================================================

/**
 * Get snapshot comparison (creation data snapshot vs current data) for an annotation.
 */
export async function getSnapshotComparison(
  studyId: string,
  annotationId: number
): Promise<SnapshotComparison> {
  const response = await api.get(`${BASE_PATH(studyId)}/${annotationId}/comparison`)
  return response.data
}

// =============================================================================
// Read Status
// =============================================================================

/**
 * Mark an annotation as read.
 */
export async function markAsRead(studyId: string, annotationId: number): Promise<Annotation> {
  const response = await api.patch(`${BASE_PATH(studyId)}/${annotationId}/read`)
  return response.data
}

// =============================================================================
// Export
// =============================================================================

/**
 * Export issues to CSV.
 */
export async function exportIssuesCsv(
  studyId: string,
  filters?: AnnotationFilters
): Promise<Blob> {
  const payload = filters
    ? {
        annotation_types: filters.annotationTypes,
        statuses: filters.statuses,
        categories: filters.categories,
        priorities: filters.priorities,
        dataset_name: filters.datasetName,
        assigned_to_user_id: filters.assignedToUserId,
        assigned_to_role: filters.assignedToRole,
        created_by: filters.createdBy,
        is_read: filters.isRead,
        source_module_key: filters.sourceModuleKey,
        source_page_key: filters.sourcePageKey,
        search_text: filters.searchText,
        created_after: filters.createdAfter,
        created_before: filters.createdBefore,
      }
    : {}

  const response = await api.post(`${BASE_PATH(studyId)}/export/csv`, payload, {
    responseType: 'blob',
  })
  return response.data
}

/**
 * Get single issue export data (for PDF generation).
 */
export async function getSingleIssueExportData(
  studyId: string,
  annotationId: number
): Promise<SingleIssueExportData> {
  const response = await api.get(`${BASE_PATH(studyId)}/issues/${annotationId}/export`)
  return response.data
}

// =============================================================================
// Metrics
// =============================================================================

/**
 * Get issue metrics summary.
 */
export async function getMetricsSummary(studyId: string): Promise<IssueMetricsSummary> {
  const response = await api.get(`${BASE_PATH(studyId)}/metrics/summary`)
  return response.data
}

/**
 * Get aging report.
 */
export async function getAgingReport(studyId: string): Promise<AgingReport> {
  const response = await api.get(`${BASE_PATH(studyId)}/metrics/aging`)
  return response.data
}

/**
 * Get issues by group (site, assignee, module, etc.).
 */
export async function getIssuesByGroup(
  studyId: string,
  groupBy: 'site' | 'assignee' | 'module' | 'category' | 'priority'
): Promise<IssuesByGroupResponse> {
  const response = await api.get(`${BASE_PATH(studyId)}/metrics/by-group/${groupBy}`)
  return response.data
}

/**
 * Get turnaround report.
 */
export async function getTurnaroundReport(studyId: string): Promise<TurnaroundReport> {
  const response = await api.get(`${BASE_PATH(studyId)}/metrics/turnaround`)
  return response.data
}

/**
 * Get user-specific issue metrics.
 */
export async function getUserMetrics(studyId: string): Promise<UserIssueMetrics> {
  const response = await api.get(`${BASE_PATH(studyId)}/metrics/user`)
  return response.data
}

// =============================================================================
// Helper: Get annotations for a specific record
// =============================================================================

/**
 * Get all annotations for a specific record.
 */
export async function getAnnotationsForRecord(
  studyId: string,
  datasetName: string,
  _recordKey: Record<string, unknown>,
  options?: {
    annotationTypes?: AnnotationType[]
  }
): Promise<AnnotationListResponse> {
  // TODO: Add record_key filtering to backend query - for now filters by dataset only
  return listIssues(studyId, {
    pageSize: 100,
    filters: {
      datasetName,
      annotationTypes: options?.annotationTypes,
    },
  })
}
